import { apiClient } from './api';
import type {
  SpringPage,
  SupportReportAttachmentKind,
  SupportReportDetail,
  SupportReportSubmit,
  SupportReportSummary,
} from '../types/supportReports';

export const SUPPORT_REPORT_LIMITS = {
  screenshots: 3,
  screenshotBytes: 8 * 1024 * 1024,
  videos: 1,
  videoBytes: 50 * 1024 * 1024,
  totalBytes: 75 * 1024 * 1024,
  videoDurationMs: 60_000,
} as const;

export const SUPPORT_REPORT_CATEGORIES = [
  ['GENERAL', 'General'],
  ['CHAT_NOTIFICATIONS', 'Chat or notifications'],
  ['ACCOUNT_LOGIN', 'Account or login'],
  ['SESSION_PAYMENT', 'Session, expense or settlement'],
  ['NAVIGATION', 'Navigation or screen'],
  ['SUGGESTION', 'Suggestion'],
  ['OTHER', 'Other'],
] as const;

export async function createSupportReportDraft(idempotencyKey: string): Promise<string> {
  const response = await apiClient.post<{ ticketReference: string }>(
    '/api/v1/support/reports/drafts',
    {},
    { headers: { 'Idempotency-Key': idempotencyKey } }
  );
  return response.data.ticketReference;
}

export async function uploadSupportReportAttachment(
  ticket: string,
  file: File,
  kind: SupportReportAttachmentKind,
  durationMs?: number
): Promise<void> {
  const reservation = await apiClient.post<{
    attachmentId: string;
    uploadUrl: string;
  }>(`/api/v1/support/reports/${encodeURIComponent(ticket)}/attachments/presign`, {
    kind,
    mimeType: file.type,
    sizeBytes: file.size,
    durationMs,
  });

  const upload = await fetch(reservation.data.uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type },
    body: file,
  });
  if (!upload.ok) throw new Error(`Attachment upload failed (${upload.status})`);

  await apiClient.post(
    `/api/v1/support/reports/${encodeURIComponent(ticket)}/attachments/${reservation.data.attachmentId}/complete`,
    {}
  );
}

export async function submitSupportReport(ticket: string, payload: SupportReportSubmit): Promise<SupportReportDetail> {
  const response = await apiClient.post<SupportReportDetail>(
    `/api/v1/support/reports/${encodeURIComponent(ticket)}/submit`,
    payload
  );
  return response.data;
}

export async function listMySupportReports(): Promise<SupportReportSummary[]> {
  const response = await apiClient.get<SpringPage<SupportReportSummary>>('/api/v1/support/reports');
  return response.data.content;
}

export async function getMySupportReport(ticket: string): Promise<SupportReportDetail> {
  const response = await apiClient.get<SupportReportDetail>(
    `/api/v1/support/reports/${encodeURIComponent(ticket)}`
  );
  return response.data;
}

export function validateSupportReportFiles(files: File[]): string | null {
  const screenshots = files.filter((file) => file.type.startsWith('image/'));
  const videos = files.filter((file) => file.type.startsWith('video/'));
  const accepted = new Set(['image/jpeg', 'image/png', 'image/webp', 'video/mp4', 'video/quicktime']);
  if (files.some((file) => !accepted.has(file.type))) return 'Use JPEG, PNG, WebP, MP4 or MOV files.';
  if (screenshots.length > SUPPORT_REPORT_LIMITS.screenshots) return 'Add no more than 3 screenshots.';
  if (videos.length > SUPPORT_REPORT_LIMITS.videos) return 'Add no more than 1 video.';
  if (screenshots.some((file) => file.size > SUPPORT_REPORT_LIMITS.screenshotBytes)) return 'Each screenshot must be 8 MB or smaller.';
  if (videos.some((file) => file.size > SUPPORT_REPORT_LIMITS.videoBytes)) return 'The video must be 50 MB or smaller.';
  if (files.reduce((total, file) => total + file.size, 0) > SUPPORT_REPORT_LIMITS.totalBytes) return 'Attachments must total 75 MB or less.';
  return null;
}

export function readVideoDuration(file: File): Promise<number> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      resolve(Math.round(video.duration * 1000));
    };
    video.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not read the selected video.'));
    };
    video.src = url;
  });
}

export type { SpringPage };
